import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useUserAccess } from '../../context/UserAccessContext';
import { isPlatformAdmin } from '../../utils/platformAdmin';
import { createEnterprise, fetchEnterprises } from '../../api/enterprises';

export default function EnterpriseManageHome() {
  const { user } = useUserAccess();
  const platformAdmin = isPlatformAdmin(user);
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchEnterprises();
      setEnterprises(res?.data?.enterprises || []);
    } catch (e) {
      setError(e?.message || 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      await createEnterprise({
        name: trimmed,
        initialAdminEmail: adminEmail.trim() || undefined,
        initialAdminPassword: adminPassword.trim() || undefined,
      });
      setName('');
      setAdminEmail('');
      setAdminPassword('');
      setShowCreate(false);
      await load();
    } catch (e) {
      setError(e?.message || 'Création impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Entreprises</title>
      </Helmet>
      <Header />
      <main className="main-content">
        <Breadcrumb items={[{ label: 'Entreprises' }]} />
        <div className="enterprise-manage-forms max-w-lg mx-auto p-4 space-y-5">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Entreprises</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {platformAdmin
                ? 'Vos organisations clientes.'
                : 'Gérez l’équipe et les rôles de votre organisation.'}
            </p>
          </div>

          {platformAdmin && !showCreate ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setShowCreate(true)}>
              Nouvelle entreprise
            </Button>
          ) : null}

          {platformAdmin && showCreate ? (
            <form onSubmit={handleCreate} className="rounded-lg border border-border p-4 space-y-3">
              <Input
                label="Nom"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex. Acme"
                autoFocus
              />
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Premier compte responsable (optionnel)
                </summary>
                <div className="mt-3 space-y-3 pl-0">
                  <Input
                    label="E-mail"
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                  />
                  <Input
                    label="Mot de passe"
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
              </details>
              <div className="flex gap-2 pt-1">
                <Button type="submit" disabled={saving || !name.trim()}>
                  {saving ? '…' : 'Créer'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}>
                  Annuler
                </Button>
              </div>
            </form>
          ) : null}

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          {loading ? (
            <p className="text-sm text-muted-foreground">Chargement…</p>
          ) : enterprises.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune entreprise.</p>
          ) : (
            <ul className="rounded-lg border border-border divide-y divide-border">
              {enterprises.map((ent) => (
                <li key={ent._id}>
                  <Link
                    to={`/enterprise-manage/${ent._id}`}
                    className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/40 transition-colors"
                  >
                    <span className="font-medium text-foreground">{ent.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">Ouvrir →</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </>
  );
}
